import { useCallback, useEffect, useRef } from "react";

/**
 * Os dois realms (ambientes de execução) desta app: o documento da shell e o do iframe do
 * softphone. Cada um tem seu próprio `navigator.mediaDevices` — objetos distintos, com listas
 * de listeners distintas —, e os dois podem ver coisas diferentes, porque permissão e captura
 * ativa são rastreadas por frame. A shell encerra as tracks logo após a sonda de permissão
 * (deliberadamente, para não segurar microfone e câmera pela sessão inteira), enquanto o
 * softphone abre os dispositivos de verdade no momento da chamada.
 */
export type DeviceRealm = "shell" | "softphone";

export type MediaDeviceEntry = {
  deviceId: string;
  kind: MediaDeviceKind;
  label: string;
  groupId: string;
};

/**
 * Origem de uma leitura. Distinguir as três é o que permite ler o rodapé do painel como
 * prova: uma atualização manual reportada como "evento" levaria à conclusão oposta da
 * verdadeira numa plataforma onde nada é entregue.
 */
export type ChangeTrigger =
  | { kind: "event"; realms: DeviceRealm[] }
  | { kind: "manual" }
  | { kind: "mount" };

export function describeTrigger(trigger: ChangeTrigger): string {
  return trigger.kind === "event" ? `event(${trigger.realms.join("+")})` : trigger.kind;
}

export type MediaDeviceChange = {
  added: MediaDeviceEntry[];
  removed: MediaDeviceEntry[];
  /** Mesma entrada `default`, apontando para outro hardware. */
  defaultChanged: MediaDeviceEntry[];
  /** Snapshot completo após a mudança. */
  devices: MediaDeviceEntry[];
  /** Linha de base, não uma mudança — primeira leitura ou re-baseline após anonimização. */
  initial: boolean;
  /** O que provocou esta leitura — evento (e de quais realms), clique ou mount. */
  trigger: ChangeTrigger;
  /** Realm de onde saiu a lista exibida. */
  enumeratedFrom: DeviceRealm;
  /** Nenhum realm devolveu rótulos — sem permissão ativa, a lista vem anonimizada. */
  anonymized: boolean;
};

export type MediaDeviceWatcherHandle = {
  /** Re-enumera sob demanda. É ação do usuário, não sondagem periódica. */
  refresh: () => void;
};

export function isMediaDeviceWatchSupported(): boolean {
  return (
    typeof navigator !== "undefined" &&
    Boolean(navigator.mediaDevices) &&
    typeof navigator.mediaDevices.enumerateDevices === "function"
  );
}

interface MediaDeviceWatcherOptions {
  /** `contentWindow` do iframe do softphone; same-origin, então dá para assinar de fora. */
  softphoneWindow?: Window | null;
  onChange?: (change: MediaDeviceChange) => void;
  onError?: (reason: string) => void;
  onPermissionState?: (state: string) => void;
}

// O `deviceId` é a chave natural, mas vem vazio enquanto não há permissão ativa. Nesse caso
// caímos para groupId + label — que também vêm vazios, mas aí o Chromium devolve uma única
// entrada por kind, então não há colisão a resolver.
export function deviceKey(device: MediaDeviceEntry): string {
  return device.deviceId
    ? `${device.kind}:${device.deviceId}`
    : `${device.kind}:${device.groupId}:${device.label}`;
}

function toEntry(device: MediaDeviceInfo): MediaDeviceEntry {
  return {
    deviceId: device.deviceId,
    kind: device.kind,
    label: device.label,
    groupId: device.groupId,
  };
}

export function shortId(deviceId: string): string {
  if (!deviceId) return "(sem id)";
  if (deviceId.length <= 12) return deviceId;
  return `${deviceId.slice(0, 8)}…`;
}

/** Acesso ao realm do iframe pode lançar (cross-origin) — nunca deixar escapar. */
function mediaDevicesOf(win: Window | null | undefined): MediaDevices | null {
  if (!win) return null;
  try {
    const md = win.navigator?.mediaDevices;
    return md && typeof md.enumerateDevices === "function" ? md : null;
  } catch {
    return null;
  }
}

/**
 * Observa a lista de dispositivos de mídia nos dois realms e reporta as mudanças. É
 * instrumentação pura: nada aqui altera roteamento de áudio — a seleção de entrada/saída vive
 * dentro do softphone, no iframe.
 *
 * Assina `devicechange` na shell **e** no `navigator.mediaDevices` do iframe, porque só a
 * shell não bastou: as mudanças apareciam apenas quando o softphone acessava os dispositivos.
 * `trigger` e `enumeratedFrom` registram a procedência de cada leitura, que é o que permite
 * distinguir "a shell não recebe o evento" de "a shell recebe mas enumera anonimizado" — e,
 * principalmente, separar uma entrega real de uma atualização manual.
 */
export function useMediaDeviceWatcher(
  enabled: boolean,
  options: MediaDeviceWatcherOptions = {}
): MediaDeviceWatcherHandle {
  const { softphoneWindow } = options;
  const previousRef = useRef<Map<string, MediaDeviceEntry> | null>(null);
  const previousAnonymizedRef = useRef(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshRef = useRef<(() => void) | null>(null);
  // Mantidos em ref para o efeito não re-assinar os listeners a cada render do componente que
  // passa os callbacks inline.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    if (!enabled || !isMediaDeviceWatchSupported()) return;

    let cancelled = false;

    const realms: Array<{ realm: DeviceRealm; md: MediaDevices }> = [];
    realms.push({ realm: "shell", md: navigator.mediaDevices });
    const softphoneMd = mediaDevicesOf(softphoneWindow);
    if (softphoneMd) {
      realms.push({ realm: "softphone", md: softphoneMd });
    } else if (softphoneWindow) {
      console.warn("[devices] mediaDevices do iframe inacessível (cross-origin?)");
    }

    const enumerate = async (md: MediaDevices): Promise<MediaDeviceEntry[] | null> => {
      try {
        return (await md.enumerateDevices()).map(toEntry);
      } catch (error) {
        // Nunca deixar escapar: uma rejeição não tratada vira linha de console capturada,
        // que vira mais um POST de log.
        console.warn("[devices] enumerateDevices falhou:", error);
        return null;
      }
    };

    const refresh = async (trigger: ChangeTrigger) => {
      const results = await Promise.all(realms.map(({ md }) => enumerate(md)));
      if (cancelled) return;

      // Escolhe a leitura mais informativa: sem permissão ativa um realm devolve a lista
      // anonimizada (uma entrada por kind, sem rótulo) enquanto o outro pode devolver tudo.
      let best: { realm: DeviceRealm; devices: MediaDeviceEntry[]; labelled: number } | null = null;
      results.forEach((devices, index) => {
        if (!devices) return;
        const labelled = devices.filter((device) => device.label).length;
        if (!best || labelled > best.labelled) {
          best = { realm: realms[index].realm, devices, labelled };
        }
      });

      if (!best) {
        optionsRef.current.onError?.("enumerateDevices falhou em todos os realms");
        return;
      }
      const { realm: enumeratedFrom, devices, labelled } = best as {
        realm: DeviceRealm;
        devices: MediaDeviceEntry[];
        labelled: number;
      };
      const anonymized = labelled === 0;

      const current = new Map(devices.map((device) => [deviceKey(device), device]));
      const previous = previousRef.current;
      const wasAnonymized = previousAnonymizedRef.current;
      previousRef.current = current;
      previousAnonymizedRef.current = anonymized;

      // Sair (ou entrar) no estado anonimizado troca todas as chaves de uma vez. Reportar isso
      // como uma enxurrada de added/removed seria ruído puro — é uma nova linha de base.
      const rebaseline = previous !== null && wasAnonymized !== anonymized;
      const initial = previous === null || rebaseline;

      const added = initial ? [] : devices.filter((device) => !previous!.has(deviceKey(device)));
      const removed = initial
        ? []
        : Array.from(previous!.values()).filter((device) => !current.has(deviceKey(device)));

      // Trocar o dispositivo padrão do sistema não altera o tamanho da lista: a entrada
      // `default` continua lá, apenas apontando para outro hardware. O groupId denuncia.
      const defaultChanged = initial
        ? []
        : devices.filter((device) => {
            if (device.deviceId !== "default") return false;
            const before = previous!.get(deviceKey(device));
            return Boolean(before) && before!.groupId !== device.groupId;
          });

      if (!initial && added.length === 0 && removed.length === 0 && defaultChanged.length === 0) {
        // O evento dispara em rajada e nem toda rajada carrega mudança observável.
        console.log(
          `[devices] change sem delta observável trigger=${describeTrigger(trigger)} from=${enumeratedFrom} total=${devices.length}`
        );
        return;
      }

      console.log(
        `[devices] ${initial ? "initial" : "change"} trigger=${describeTrigger(trigger)} from=${enumeratedFrom}` +
          ` anonymized=${anonymized} added=${added.length} removed=${removed.length}` +
          ` defaultChanged=${defaultChanged.length} total=${devices.length}`
      );
      devices.forEach((device) => {
        console.log(
          `[devices]   ${device.kind} id=${shortId(device.deviceId)} group=${shortId(device.groupId)} label="${device.label}"`
        );
      });
      removed.forEach((device) => {
        console.log(`[devices]   removido ${device.kind} label="${device.label}"`);
      });

      optionsRef.current.onChange?.({
        added,
        removed,
        defaultChanged,
        devices,
        initial,
        trigger,
        enumeratedFrom,
        anonymized,
      });
    };

    // O evento não tem payload — ele só avisa "a lista mudou", e o delta sai da comparação com
    // o snapshot anterior. Um headset USB registra entrada e saída, e o Chrome dispara um
    // evento por dispositivo; além disso o mesmo evento do SO chega pelos dois realms. O
    // debounce é compartilhado justamente para os dois casos colapsarem num item só.
    // Realms que dispararam dentro da janela do debounce corrente. É um conjunto porque o
    // mesmo evento do SO pode chegar pelos dois, e creditar só o último apagaria essa
    // informação — justamente a que distingue "a shell não recebe" de "a shell recebe também".
    // Local ao efeito: listeners e debounce são recriados junto, então herdar pendências de
    // uma assinatura anterior só produziria crédito errado.
    const pendingRealms = new Set<DeviceRealm>();

    const schedule = (realm: DeviceRealm) => {
      pendingRealms.add(realm);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        debounceRef.current = null;
        // Ordem fixa (shell antes de softphone) para o texto do rodapé ficar estável.
        const fired = (["shell", "softphone"] as DeviceRealm[]).filter((candidate) =>
          pendingRealms.has(candidate)
        );
        pendingRealms.clear();
        void refresh({ kind: "event", realms: fired });
      }, 300);
    };

    // Clique não passa pelo debounce: não há rajada a colapsar, e atrasar 300 ms só atrapalha
    // o teste do headset com fio, onde o esperado é justamente nada mudar.
    refreshRef.current = () => void refresh({ kind: "manual" });

    const listeners = realms.map(({ realm, md }) => {
      const handler = () => {
        console.log(`[devices] devicechange recebido em ${realm}`);
        schedule(realm);
      };
      md.addEventListener("devicechange", handler);
      return { md, handler };
    });

    console.log(`[devices] observando realms: ${realms.map((r) => r.realm).join(", ")}`);
    void refresh({ kind: "mount" });

    return () => {
      cancelled = true;
      refreshRef.current = null;
      pendingRealms.clear();
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      listeners.forEach(({ md, handler }) => {
        try {
          md.removeEventListener("devicechange", handler);
        } catch {
          // realm já descartado junto com o iframe
        }
      });
    };
  }, [enabled, softphoneWindow]);

  // Diagnóstico da permissão, orientado a evento (não é poll): se o estado cair de `granted`
  // depois que a sonda encerra as tracks, isso explica a lista anonimizada na shell.
  useEffect(() => {
    if (!enabled || typeof navigator === "undefined" || !navigator.permissions) return;

    let cancelled = false;

    const report = (state: string) => {
      console.log(`[devices] permissão de microfone: ${state}`);
      optionsRef.current.onPermissionState?.(state);
    };

    navigator.permissions
      // `microphone` não está no union de PermissionName do TS 4.1 e o WebKit sequer o
      // suporta — daí o cast e o catch.
      .query({ name: "microphone" as PermissionName })
      .then((result) => {
        if (cancelled) return;
        report(result.state);
        result.addEventListener("change", () => report(result.state));
      })
      .catch((error) => {
        console.warn("[devices] permissions.query(microphone) indisponível:", error);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const refresh = useCallback(() => {
    console.log("[devices] atualização manual solicitada");
    refreshRef.current?.();
  }, []);

  return { refresh };
}
